/**
 * Action Types and Action Creators for DbBrowser
 * Handles all database-related operations
 */

// Action Types
export const ActionTypes = {
    // Loading DBGroups
    LOAD_DBGROUPS_START: 'DBBROWSER/LOAD_DBGROUPS_START',
    LOAD_DBGROUPS_SUCCESS: 'DBBROWSER/LOAD_DBGROUPS_SUCCESS',
    LOAD_DBGROUPS_ERROR: 'DBBROWSER/LOAD_DBGROUPS_ERROR',
    
    // Loading Databases
    LOAD_DATABASES_START: 'DBBROWSER/LOAD_DATABASES_START',
    LOAD_DATABASES_SUCCESS: 'DBBROWSER/LOAD_DATABASES_SUCCESS',
    LOAD_DATABASES_ERROR: 'DBBROWSER/LOAD_DATABASES_ERROR',
    
    // Fetching accessible items from server
    FETCH_ACCESSIBLE_ITEMS_START: 'DBBROWSER/FETCH_ACCESSIBLE_ITEMS_START',
    FETCH_ACCESSIBLE_ITEMS_SUCCESS: 'DBBROWSER/FETCH_ACCESSIBLE_ITEMS_SUCCESS',
    FETCH_ACCESSIBLE_ITEMS_ERROR: 'DBBROWSER/FETCH_ACCESSIBLE_ITEMS_ERROR',
    
    // Sync status check
    CHECK_SYNC_STATUS_START: 'DBBROWSER/CHECK_SYNC_STATUS_START',
    CHECK_SYNC_STATUS_SUCCESS: 'DBBROWSER/CHECK_SYNC_STATUS_SUCCESS',
    CHECK_SYNC_STATUS_ERROR: 'DBBROWSER/CHECK_SYNC_STATUS_ERROR',
    
    // Push DBGroup to server
    PUSH_DBGROUP_START: 'DBBROWSER/PUSH_DBGROUP_START',
    PUSH_DBGROUP_SUCCESS: 'DBBROWSER/PUSH_DBGROUP_SUCCESS',
    PUSH_DBGROUP_ERROR: 'DBBROWSER/PUSH_DBGROUP_ERROR',
    
    // Push Database to server
    PUSH_DATABASE_START: 'DBBROWSER/PUSH_DATABASE_START',
    PUSH_DATABASE_SUCCESS: 'DBBROWSER/PUSH_DATABASE_SUCCESS',
    PUSH_DATABASE_ERROR: 'DBBROWSER/PUSH_DATABASE_ERROR',
    
    // Download Database from server
    DOWNLOAD_DATABASE_START: 'DBBROWSER/DOWNLOAD_DATABASE_START',
    DOWNLOAD_DATABASE_SUCCESS: 'DBBROWSER/DOWNLOAD_DATABASE_SUCCESS',
    DOWNLOAD_DATABASE_ERROR: 'DBBROWSER/DOWNLOAD_DATABASE_ERROR',
    
    // Download DBGroup from server
    DOWNLOAD_DBGROUP_START: 'DBBROWSER/DOWNLOAD_DBGROUP_START',
    DOWNLOAD_DBGROUP_SUCCESS: 'DBBROWSER/DOWNLOAD_DBGROUP_SUCCESS',
    DOWNLOAD_DBGROUP_ERROR: 'DBBROWSER/DOWNLOAD_DBGROUP_ERROR',
    
    // Fetch user names for access control
    FETCH_USER_NAMES_START: 'DBBROWSER/FETCH_USER_NAMES_START',
    FETCH_USER_NAMES_SUCCESS: 'DBBROWSER/FETCH_USER_NAMES_SUCCESS',
    FETCH_USER_NAMES_ERROR: 'DBBROWSER/FETCH_USER_NAMES_ERROR',
    
    // Update single DBGroup (after push or sync)
    UPDATE_DBGROUP: 'DBBROWSER/UPDATE_DBGROUP',
    
    // Update single Database (after push or sync)
    UPDATE_DATABASE: 'DBBROWSER/UPDATE_DATABASE',
    
    // Clear errors
    CLEAR_ERROR: 'DBBROWSER/CLEAR_ERROR',
    
    // Search users for permission management
    SEARCH_USERS_START: 'DBBROWSER/SEARCH_USERS_START',
    SEARCH_USERS_SUCCESS: 'DBBROWSER/SEARCH_USERS_SUCCESS',
    SEARCH_USERS_ERROR: 'DBBROWSER/SEARCH_USERS_ERROR',
    
    // Update Database Permissions
    UPDATE_DB_PERMISSIONS_START: 'DBBROWSER/UPDATE_DB_PERMISSIONS_START',
    UPDATE_DB_PERMISSIONS_SUCCESS: 'DBBROWSER/UPDATE_DB_PERMISSIONS_SUCCESS',
    UPDATE_DB_PERMISSIONS_ERROR: 'DBBROWSER/UPDATE_DB_PERMISSIONS_ERROR',
    
    // Update DBGroup Permissions
    UPDATE_DBGROUP_PERMISSIONS_START: 'DBBROWSER/UPDATE_DBGROUP_PERMISSIONS_START',
    UPDATE_DBGROUP_PERMISSIONS_SUCCESS: 'DBBROWSER/UPDATE_DBGROUP_PERMISSIONS_SUCCESS',
    UPDATE_DBGROUP_PERMISSIONS_ERROR: 'DBBROWSER/UPDATE_DBGROUP_PERMISSIONS_ERROR',
    
    // Yjs real-time sync (per database)
    YJS_SYNC_STATUS_UPDATE: 'DBBROWSER/YJS_SYNC_STATUS_UPDATE',
    YJS_PENDING_CHANGES_UPDATE: 'DBBROWSER/YJS_PENDING_CHANGES_UPDATE',
};

// Database name constant
const DB_NAME = 'ShepherdDB';

// Helper function to open database with automatic versioning
const openDatabase = () => {
    return new Promise((resolve, reject) => {
        // First, open without version to check current state
        const checkRequest = indexedDB.open(DB_NAME);
        
        checkRequest.onsuccess = (event) => {
            const db = event.target.result;
            const currentVersion = db.version;
            const needsUpgrade = !db.objectStoreNames.contains('DBgroups') || 
                                 !db.objectStoreNames.contains('Databases');
            
            // Check if DBgroups has the name index (for existing schemas)
            let needsIndex = false;
            if (db.objectStoreNames.contains('DBgroups')) {
                const transaction = db.transaction(['DBgroups'], 'readonly');
                const objectStore = transaction.objectStore('DBgroups');
                needsIndex = !objectStore.indexNames.contains('name');
            }
            
            // Note: Yjs state fields (yjs_state, yjs_state_vector) are added dynamically as records are created
            // IndexedDB doesn't require schema definition for fields, only object stores need definition
            
            db.close();
            
            if (!needsUpgrade && !needsIndex) {
                // Everything exists, open normally
                const request = indexedDB.open(DB_NAME);
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
                return;
            }
            
            // Need to upgrade, open with new version
            const request = indexedDB.open(DB_NAME, currentVersion + 1);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                
                // Create DBgroups object store if it doesn't exist
                if (!db.objectStoreNames.contains('DBgroups')) {
                    const dbGroupStore = db.createObjectStore('DBgroups', { keyPath: 'id', autoIncrement: true });
                    dbGroupStore.createIndex('name', 'name', { unique: false });
                    console.log('[DBBROWSER] Created DBgroups object store with name index');
                } else if (needsIndex) {
                    // Add index to existing store
                    const transaction = event.target.transaction;
                    const objectStore = transaction.objectStore('DBgroups');
                    if (!objectStore.indexNames.contains('name')) {
                        objectStore.createIndex('name', 'name', { unique: false });
                        console.log('[DBBROWSER] Added name index to existing DBgroups');
                    }
                }
                
                // Create Databases object store if it doesn't exist
                if (!db.objectStoreNames.contains('Databases')) {
                    db.createObjectStore('Databases', { keyPath: 'id', autoIncrement: true });
                    console.log('[DBBROWSER] Created Databases object store');
                }
            };
        };
        
        checkRequest.onerror = () => {
            // Database doesn't exist yet, create with version 1
            const request = indexedDB.open(DB_NAME, 1);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                
                // Create DBgroups object store
                const dbGroupStore = db.createObjectStore('DBgroups', { keyPath: 'id', autoIncrement: true });
                dbGroupStore.createIndex('name', 'name', { unique: false });
                console.log('[DBBROWSER] Created DBgroups object store with name index');
                
                // Create Databases object store
                db.createObjectStore('Databases', { keyPath: 'id', autoIncrement: true });
                console.log('[DBBROWSER] Created Databases object store');
            };
        };
    });
};

// Action Creators

// Load DBGroups from IndexedDB
export const loadDbGroups = () => {
    return async (dispatch) => {
        dispatch({ type: ActionTypes.LOAD_DBGROUPS_START });
        
        try {
            const db = await openDatabase();
                
            // Check if object store exists
            if (!db.objectStoreNames.contains('DBgroups')) {
                console.warn('[DBBROWSER] DBgroups object store not found in legacy DB, initializing with empty list');
                db.close();
                dispatch({
                    type: ActionTypes.LOAD_DBGROUPS_SUCCESS,
                    payload: []
                });
                return;
            }
            
            const transaction = db.transaction(['DBgroups'], 'readonly');
                const objectStore = transaction.objectStore('DBgroups');
                const getAllRequest = objectStore.getAll();
                
                getAllRequest.onsuccess = () => {
                    const groups = getAllRequest.result.map(group => ({
                        ...group,
                        storageType: group.storageType || 'local',
                        version: group.version || 1
                    }));
                    
                    dispatch({
                        type: ActionTypes.LOAD_DBGROUPS_SUCCESS,
                        payload: groups
                    });
                    
                    console.log('[DBBROWSER] Loaded DBGroups:', groups);
                };
                
            getAllRequest.onerror = (error) => {
                dispatch({
                    type: ActionTypes.LOAD_DBGROUPS_ERROR,
                    payload: error.toString()
                });
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        } catch (error) {
            dispatch({
                type: ActionTypes.LOAD_DBGROUPS_ERROR,
                payload: error.toString()
            });
        }
    };
};

// Load Databases from IndexedDB
export const loadDatabases = () => {
    return async (dispatch) => {
        dispatch({ type: ActionTypes.LOAD_DATABASES_START });
        
        try {
            const db = await openDatabase();
                
            // Check if object store exists
            if (!db.objectStoreNames.contains('Databases')) {
                console.warn('[DBBROWSER] Databases object store not found in legacy DB, initializing with empty list');
                db.close();
                dispatch({
                    type: ActionTypes.LOAD_DATABASES_SUCCESS,
                    payload: []
                });
                return;
            }
            
            const transaction = db.transaction(['Databases'], 'readonly');
                const objectStore = transaction.objectStore('Databases');
                const getAllRequest = objectStore.getAll();
                
                getAllRequest.onsuccess = () => {
                    const dbs = getAllRequest.result.map(database => ({
                        ...database,
                        storageType: database.storageType || 'local',
                        version: database.version || 1,
                        recordsCount: database.records?.length || 0
                    }));
                    
                    dispatch({
                        type: ActionTypes.LOAD_DATABASES_SUCCESS,
                        payload: dbs
                    });
                    
                    console.log('[DBBROWSER] Loaded Databases:', dbs);
                };
                
            getAllRequest.onerror = (error) => {
                dispatch({
                    type: ActionTypes.LOAD_DATABASES_ERROR,
                    payload: error.toString()
                });
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        } catch (error) {
            dispatch({
                type: ActionTypes.LOAD_DATABASES_ERROR,
                payload: error.toString()
            });
        }
    };
};

// Fetch accessible items from server
export const fetchAccessibleItems = (userEmail) => {
    return async (dispatch) => {
        if (!userEmail) {
            dispatch({
                type: ActionTypes.FETCH_ACCESSIBLE_ITEMS_SUCCESS,
                payload: { databases: [], dbgroups: [] }
            });
            return;
        }
        
        dispatch({ type: ActionTypes.FETCH_ACCESSIBLE_ITEMS_START });
        
        try {
            const url = window.devMode ? 'http://localhost:80/get_accessible_items' : '/get_accessible_items';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: userEmail })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success') {
                    dispatch({
                        type: ActionTypes.FETCH_ACCESSIBLE_ITEMS_SUCCESS,
                        payload: {
                            databases: data.databases || [],
                            dbgroups: data.dbgroups || []
                        }
                    });
                    console.log('[DBBROWSER] Loaded accessible items from server:', data);
                } else {
                    throw new Error(data.message || 'Failed to fetch accessible items');
                }
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            console.error('[DBBROWSER] Error fetching accessible items:', error);
            dispatch({
                type: ActionTypes.FETCH_ACCESSIBLE_ITEMS_ERROR,
                payload: error.toString()
            });
        }
    };
};

// Check sync status for global items
export const checkSyncStatus = (userEmail, dbGroups, databases) => {
    return async (dispatch) => {
        if (!userEmail) return;
        
        // Only check global items
        const globalGroups = dbGroups.filter(g => g.storageType === 'global');
        const globalDatabases = databases.filter(d => d.storageType === 'global');
        
        if (globalGroups.length === 0 && globalDatabases.length === 0) return;
        
        dispatch({ type: ActionTypes.CHECK_SYNC_STATUS_START });
        
        try {
            const url = window.devMode ? 'http://localhost:80/check_sync_status' : '/check_sync_status';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    dbgroups: globalGroups.map(g => ({ name: g.name })),
                    databases: globalDatabases.map(d => ({ name: d.name, group: d.dbGroupName }))
                })
            });

            if (response.ok) {
                const result = await response.json();
                
                dispatch({
                    type: ActionTypes.CHECK_SYNC_STATUS_SUCCESS,
                    payload: result
                });
                
                console.log('[DBBROWSER] Sync status checked successfully');
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            console.error('[DBBROWSER] Error checking sync status:', error);
            dispatch({
                type: ActionTypes.CHECK_SYNC_STATUS_ERROR,
                payload: error.toString()
            });
        }
    };
};

// Push DBGroup to server
export const pushDbGroup = (userEmail, group, language) => {
    return async (dispatch) => {
        if (!userEmail) {
            alert('Please login to push to server');
            return;
        }
        
        dispatch({
            type: ActionTypes.PUSH_DBGROUP_START,
            payload: { id: `group-${group.id}` }
        });
        
        try {
            const url = window.devMode ? 'http://localhost:80/push_dbgroup' : '/push_dbgroup';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    dbgroup: {
                        name: group.name,
                        description: `Created: ${group.timestamp}`,
                        fields: group.fields,
                        totalFields: group.totalFields,
                        timestamp: group.timestamp
                    }
                })
            });

            const result = await response.json();

            if (response.ok) {
                console.log('[DBBROWSER] DBGroup pushed successfully:', group.name);
                
                const updatedGroup = { 
                    ...group, 
                    storageType: 'global', 
                    version: result.version,
                    owners: result.owners || '',
                    admins: result.admins || '',
                    contributors: result.contributors || '',
                    readers: result.readers || '',
                    supervised: result.supervised || ''
                };
                
                dispatch({
                    type: ActionTypes.PUSH_DBGROUP_SUCCESS,
                    payload: updatedGroup
                });
                
                alert(`✅ ${result.message}`);
            } else {
                throw new Error(result.message || 'Failed to push DBGroup');
            }
        } catch (error) {
            console.error('[DBBROWSER] Error pushing DBGroup:', error);
            dispatch({
                type: ActionTypes.PUSH_DBGROUP_ERROR,
                payload: error.toString()
            });
            alert(`❌ Error: ${error.message}`);
        }
    };
};

// Push Database to server
export const pushDatabase = (userEmail, database, language) => {
    return async (dispatch) => {
        if (!userEmail) {
            alert('Please login to push to server');
            return;
        }
        
        dispatch({
            type: ActionTypes.PUSH_DATABASE_START,
            payload: { id: `db-${database.id}` }
        });
        
        try {
            const url = window.devMode ? 'http://localhost:80/push_database' : '/push_database';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    database: {
                        name: database.name,
                        dbGroupName: database.dbGroupName,
                        records: database.records,
                        version: database.version,
                        timestamp: database.timestamp
                    }
                })
            });

            const result = await response.json();

            if (response.ok) {
                console.log('[DBBROWSER] Database pushed successfully:', database.name);
                
                const updatedDatabase = { 
                    ...database, 
                    storageType: 'global', 
                    version: result.version,
                    owners: result.owners || '',
                    admins: result.admins || '',
                    contributors: result.contributors || '',
                    readers: result.readers || '',
                    supervised: result.supervised || ''
                };
                
                dispatch({
                    type: ActionTypes.PUSH_DATABASE_SUCCESS,
                    payload: updatedDatabase
                });
                
                alert(`✅ ${result.message}`);
            } else {
                throw new Error(result.message || 'Failed to push Database');
            }
        } catch (error) {
            console.error('[DBBROWSER] Error pushing Database:', error);
            dispatch({
                type: ActionTypes.PUSH_DATABASE_ERROR,
                payload: error.toString()
            });
            alert(`❌ Error: ${error.message}`);
        }
    };
};

// Download Database from server
export const downloadDatabase = (userEmail, serverDatabase, language) => {
    return async (dispatch) => {
        if (!userEmail) {
            alert('Please login first');
            return;
        }
        
        dispatch({
            type: ActionTypes.DOWNLOAD_DATABASE_START,
            payload: { id: `db-${serverDatabase.name}` }
        });
        
        try {
            // First, check if the DBGroup for this database exists locally
            const dbGroupName = serverDatabase.dbGroupName || serverDatabase.group;
            
            if (!dbGroupName) {
                console.warn('[DBBROWSER] No DBGroup specified for database:', serverDatabase.name);
            } else {
                // Check if DBGroup exists locally
                await new Promise(async (resolve, reject) => {
                    try {
                        const db = await openDatabase();
                        
                        if (db.objectStoreNames.contains('DBgroups')) {
                            const transaction = db.transaction(['DBgroups'], 'readonly');
                            const objectStore = transaction.objectStore('DBgroups');
                            const index = objectStore.index('name');
                            const getRequest = index.get(dbGroupName);
                            
                            getRequest.onsuccess = async () => {
                                const existingDbGroup = getRequest.result;
                                db.close();
                                
                                if (!existingDbGroup) {
                                    console.log('[DBBROWSER] DBGroup not found locally, downloading first:', dbGroupName);
                                    
                                    // Download the DBGroup first (silently, without alert)
                                    try {
                                        await dispatch(downloadDbGroup(userEmail, { name: dbGroupName }, language, true));
                                        console.log('[DBBROWSER] DBGroup downloaded successfully, proceeding with database download');
                                    } catch (error) {
                                        console.error('[DBBROWSER] Failed to download DBGroup:', error);
                                        // Continue with database download even if DBGroup download fails
                                    }
                                } else {
                                    console.log('[DBBROWSER] DBGroup already exists locally:', dbGroupName);
                                }
                                resolve();
                            };
                            
                            getRequest.onerror = () => {
                                db.close();
                                resolve(); // Continue even if check fails
                            };
                        } else {
                            db.close();
                            resolve();
                        }
                    } catch (error) {
                        console.error('[DBBROWSER] Error checking DBGroup:', error);
                        resolve(); // Continue even if check fails
                    }
                });
            }
            
            // Now proceed with downloading the database
            const db = await openDatabase();
                
                // Check if object store exists
                if (!db.objectStoreNames.contains('Databases')) {
                    console.error('[DBBROWSER] Databases object store not found');
                    dispatch({
                        type: ActionTypes.DOWNLOAD_DATABASE_ERROR,
                        payload: 'Database not properly initialized'
                    });
                    db.close();
                    alert('❌ Database not properly initialized. Please refresh the page.');
                    return;
                }
                
                const transaction = db.transaction(['Databases'], 'readwrite');
                const objectStore = transaction.objectStore('Databases');
                
                // First, check if database with same name and dbGroupName already exists
                const getAllRequest = objectStore.getAll();
                
                getAllRequest.onsuccess = () => {
                    const existingDatabases = getAllRequest.result;
                    const existingDb = existingDatabases.find(db => 
                        db.name === serverDatabase.name && 
                        db.dbGroupName === serverDatabase.dbGroupName
                    );
                    
                    if (existingDb) {
                        // Database already exists, update it instead
                        console.log('[DBBROWSER] Database already exists, updating:', serverDatabase.name);
                        
                        const updatedDatabase = {
                            ...existingDb,
                            records: serverDatabase.records,
                            version: serverDatabase.version,
                            storageType: 'global',
                            timestamp: Date.now(),
                            syncStatus: 'synced',
                            owners: serverDatabase.owners,
                            admins: serverDatabase.admins,
                            contributors: serverDatabase.contributors,
                            readers: serverDatabase.readers
                        };
                        
                        const updateRequest = objectStore.put(updatedDatabase);
                        
                        updateRequest.onsuccess = () => {
                            db.close();
                            console.log('[DBBROWSER] Database updated:', serverDatabase.name);
                            
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DATABASE_SUCCESS,
                                payload: updatedDatabase
                            });
                            
                            alert(`✅ Updated successfully: ${serverDatabase.name}`);
                        };
                        
                        updateRequest.onerror = (error) => {
                            db.close();
                            console.error('[DBBROWSER] Error updating database:', error);
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DATABASE_ERROR,
                                payload: error.toString()
                            });
                            alert(`❌ Error updating database`);
                        };
                    } else {
                        // Database doesn't exist, add new one
                        const newDatabase = {
                            name: serverDatabase.name,
                            dbGroupName: serverDatabase.dbGroupName,
                            records: serverDatabase.records,
                            version: serverDatabase.version,
                            storageType: 'global',
                            timestamp: Date.now(),
                            syncStatus: 'synced',
                            owners: serverDatabase.owners,
                            admins: serverDatabase.admins,
                            contributors: serverDatabase.contributors,
                            readers: serverDatabase.readers,
                    supervised: serverDatabase.supervised
                        };
                        
                        const addRequest = objectStore.add(newDatabase);
                        
                        addRequest.onsuccess = () => {
                            db.close();
                            // Get the auto-generated ID
                            const generatedId = addRequest.result;
                            const savedDatabase = { ...newDatabase, id: generatedId };
                            
                            console.log('[DBBROWSER] Database downloaded:', serverDatabase.name);
                            
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DATABASE_SUCCESS,
                                payload: savedDatabase
                            });
                            
                            alert(`✅ Downloaded successfully: ${serverDatabase.name}`);
                        };
                        
                        addRequest.onerror = (error) => {
                            db.close();
                            console.error('[DBBROWSER] Error adding database:', error);
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DATABASE_ERROR,
                                payload: error.toString()
                            });
                            alert(`❌ Error saving database: ${error}`);
                        };
                    }
                };
                
                getAllRequest.onerror = (error) => {
                    db.close();
                    console.error('[DBBROWSER] Error checking existing databases:', error);
                    dispatch({
                        type: ActionTypes.DOWNLOAD_DATABASE_ERROR,
                        payload: error.toString()
                    });
                    alert(`❌ Error checking existing databases`);
                };
        } catch (error) {
            console.error('[DBBROWSER] Error downloading database:', error);
            dispatch({
                type: ActionTypes.DOWNLOAD_DATABASE_ERROR,
                payload: error.toString()
            });
            alert(`❌ Error downloading database`);
        }
    };
};

// Download DBGroup from server
export const downloadDbGroup = (userEmail, serverDbGroup, language, silent = false) => {
    return async (dispatch) => {
        if (!userEmail) {
            if (!silent) alert('Please login first');
            return Promise.reject(new Error('User not logged in'));
        }
        
        dispatch({
            type: ActionTypes.DOWNLOAD_DBGROUP_START,
            payload: { id: `dbgroup-${serverDbGroup.name}` }
        });
        
        return new Promise(async (resolve, reject) => {
            try {
                // Fetch the complete DBGroup including field definitions from server
                const url = window.devMode ? 'http://localhost:80/get_dbgroup' : '/get_dbgroup';
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        email: userEmail,
                        name: serverDbGroup.name 
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                
                if (data.status !== 'success') {
                    throw new Error(data.message || 'Failed to fetch DBGroup');
                }

                const dbgroupData = data.dbgroup;
                
                // Now save to IndexedDB
                const db = await openDatabase();
                
                // Check if object store exists
                if (!db.objectStoreNames.contains('DBgroups')) {
                    console.error('[DBBROWSER] DBgroups object store not found');
                    dispatch({
                        type: ActionTypes.DOWNLOAD_DBGROUP_ERROR,
                        payload: 'Database not properly initialized'
                    });
                    db.close();
                    if (!silent) alert('❌ Database not properly initialized. Please refresh the page.');
                    reject(new Error('Database not properly initialized'));
                    return;
                }
                
                const transaction = db.transaction(['DBgroups'], 'readwrite');
                const objectStore = transaction.objectStore('DBgroups');
                
                // Check if DBGroup with same name already exists
                const index = objectStore.index('name');
                const getRequest = index.get(dbgroupData.name);
                
                getRequest.onsuccess = () => {
                    const existingDbGroup = getRequest.result;
                    
                    if (existingDbGroup) {
                        // DBGroup already exists, update it instead
                        console.log('[DBBROWSER] DBGroup already exists, updating:', dbgroupData.name);
                        
                        const updatedDbGroup = {
                            ...existingDbGroup,
                            description: dbgroupData.description || '',
                            fields: dbgroupData.fields || [],
                            version: dbgroupData.version || 1,
                            storageType: 'global',
                            timestamp: Date.now(),
                            syncStatus: 'synced',
                            owners: dbgroupData.owners || '',
                            admins: dbgroupData.admins || '',
                            contributors: dbgroupData.contributors || '',
                            readers: dbgroupData.readers || ''
                        };
                        
                        const updateRequest = objectStore.put(updatedDbGroup);
                        
                        updateRequest.onsuccess = () => {
                            db.close();
                            console.log('[DBBROWSER] DBGroup updated:', dbgroupData.name);
                            
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DBGROUP_SUCCESS,
                                payload: updatedDbGroup
                            });
                            
                            if (!silent) alert(`✅ Updated successfully: ${dbgroupData.name}`);
                            resolve(updatedDbGroup);
                        };
                        
                        updateRequest.onerror = (error) => {
                            db.close();
                            console.error('[DBBROWSER] Error updating DBGroup:', error);
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DBGROUP_ERROR,
                                payload: error.toString()
                            });
                            if (!silent) alert(`❌ Error updating DBGroup`);
                            reject(error);
                        };
                    } else {
                        // DBGroup doesn't exist, add new one
                        const newDbGroup = {
                            name: dbgroupData.name,
                            description: dbgroupData.description || '',
                            fields: dbgroupData.fields || [],
                            version: dbgroupData.version || 1,
                            storageType: 'global',
                            timestamp: Date.now(),
                            syncStatus: 'synced',
                            owners: dbgroupData.owners || '',
                            admins: dbgroupData.admins || '',
                            contributors: dbgroupData.contributors || '',
                            readers: dbgroupData.readers || ''
                        };
                        
                        const addRequest = objectStore.add(newDbGroup);
                        
                        addRequest.onsuccess = () => {
                            db.close();
                            // Get the auto-generated ID
                            const generatedId = addRequest.result;
                            const savedDbGroup = { ...newDbGroup, id: generatedId };
                            
                            console.log('[DBBROWSER] DBGroup downloaded:', dbgroupData.name);
                            
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DBGROUP_SUCCESS,
                                payload: savedDbGroup
                            });
                            
                            if (!silent) alert(`✅ Downloaded successfully: ${dbgroupData.name}`);
                            resolve(savedDbGroup);
                        };
                        
                        addRequest.onerror = (error) => {
                            db.close();
                            console.error('[DBBROWSER] Error adding DBGroup:', error);
                            dispatch({
                                type: ActionTypes.DOWNLOAD_DBGROUP_ERROR,
                                payload: error.toString()
                            });
                            if (!silent) alert(`❌ Error saving DBGroup: ${error}`);
                            reject(error);
                        };
                    }
                };
                
                getRequest.onerror = (error) => {
                    db.close();
                    console.error('[DBBROWSER] Error checking existing DBGroup:', error);
                    dispatch({
                        type: ActionTypes.DOWNLOAD_DBGROUP_ERROR,
                        payload: error.toString()
                    });
                    if (!silent) alert(`❌ Error checking existing DBGroup`);
                    reject(error);
                };
            } catch (error) {
                console.error('[DBBROWSER] Error downloading DBGroup:', error);
                dispatch({
                    type: ActionTypes.DOWNLOAD_DBGROUP_ERROR,
                    payload: error.toString()
                });
                if (!silent) alert(`❌ Error downloading DBGroup: ${error.message}`);
                reject(error);
            }
        });
    };
};

// Fetch user names for access control display
export const fetchUserNames = (userEmail, uids) => {
    return async (dispatch) => {
        if (!userEmail || !uids || uids.length === 0) {
            return;
        }
        
        dispatch({ type: ActionTypes.FETCH_USER_NAMES_START });
        
        try {
            const url = window.devMode ? 'http://localhost:80/get_user_names' : '/get_user_names';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    uids: uids
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success') {
                    dispatch({
                        type: ActionTypes.FETCH_USER_NAMES_SUCCESS,
                        payload: data.users
                    });
                } else {
                    throw new Error(data.message || 'Failed to fetch user names');
                }
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            console.error('[DBBROWSER] Error fetching user names:', error);
            dispatch({
                type: ActionTypes.FETCH_USER_NAMES_ERROR,
                payload: error.toString()
            });
        }
    };
};

// Update single DBGroup
export const updateDbGroup = (group) => ({
    type: ActionTypes.UPDATE_DBGROUP,
    payload: group
});

// Update single Database
export const updateDatabase = (database) => ({
    type: ActionTypes.UPDATE_DATABASE,
    payload: database
});

// Clear specific error
export const clearError = (errorKey) => ({
    type: ActionTypes.CLEAR_ERROR,
    payload: errorKey
});

// Search users for permission management
export const searchUsers = (userEmail, searchQuery) => {
    return async (dispatch) => {
        if (!searchQuery || searchQuery.length < 2) {
            return;
        }

        dispatch({ type: ActionTypes.SEARCH_USERS_START });

        try {
            const url = window.devMode ? 'http://localhost:80/search_users' : '/search_users';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    query: searchQuery
                })
            });

            const result = await response.json();

            if (response.ok) {
                dispatch({
                    type: ActionTypes.SEARCH_USERS_SUCCESS,
                    payload: result.users || []
                });
                return result.users || [];
            } else {
                throw new Error(result.error || 'Failed to search users');
            }
        } catch (error) {
            console.error('[DBBROWSER] Error searching users:', error);
            dispatch({
                type: ActionTypes.SEARCH_USERS_ERROR,
                payload: error.toString()
            });
            alert('Error searching users');
            return [];
        }
    };
};

// Update Database Permissions
export const updateDatabasePermissions = (userEmail, databaseName, dbGroupName, permissionType, action, targetUid) => {
    return async (dispatch) => {
        dispatch({ type: ActionTypes.UPDATE_DB_PERMISSIONS_START });

        try {
            const url = window.devMode ? 'http://localhost:80/update_database_permissions' : '/update_database_permissions';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    database_name: databaseName,
                    db_group_name: dbGroupName,
                    permission_type: permissionType,
                    action: action,
                    target_uid: targetUid
                })
            });

            const result = await response.json();

            if (response.ok) {
                dispatch({
                    type: ActionTypes.UPDATE_DB_PERMISSIONS_SUCCESS,
                    payload: {
                        databaseName,
                        dbGroupName,
                        permissions: result.permissions,
                        version: result.version
                    }
                });
                return result;
            } else {
                throw new Error(result.error || 'Failed to update permissions');
            }
        } catch (error) {
            console.error('[DBBROWSER] Error updating database permissions:', error);
            dispatch({
                type: ActionTypes.UPDATE_DB_PERMISSIONS_ERROR,
                payload: error.toString()
            });
            throw error;
        }
    };
};

// Update DBGroup Permissions
export const updateDbGroupPermissions = (userEmail, dbGroupName, permissionType, action, targetUid) => {
    return async (dispatch) => {
        dispatch({ type: ActionTypes.UPDATE_DBGROUP_PERMISSIONS_START });

        try {
            const url = window.devMode ? 'http://localhost:80/update_dbgroup_permissions' : '/update_dbgroup_permissions';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    dbgroup_name: dbGroupName,
                    permission_type: permissionType,
                    action: action,
                    target_uid: targetUid
                })
            });

            const result = await response.json();

            if (response.ok) {
                dispatch({
                    type: ActionTypes.UPDATE_DBGROUP_PERMISSIONS_SUCCESS,
                    payload: {
                        dbGroupName,
                        permissions: result.permissions,
                        version: result.version
                    }
                });
                return result;
            } else {
                throw new Error(result.error || 'Failed to update permissions');
            }
        } catch (error) {
            console.error('[DBBROWSER] Error updating DBGroup permissions:', error);
            dispatch({
                type: ActionTypes.UPDATE_DBGROUP_PERMISSIONS_ERROR,
                payload: error.toString()
            });
            throw error;
        }
    };
};
